import { getCountryFlag } from '@home/_config/countries';

import type { ICascaderSelectChangeContext } from '@home/components/CascaderSelect';
import type { ICountry, IRegion } from '@home/types/common';
import type { IRegionCascaderSelectChangeContext, IRegionCascaderSelectOption } from './index';

export const REGION_CASCADER_SELECT_LOCALE_KEYS = {
  label: 'dbh_country',
  placeholder: 'dbh_region_cascader_select_placeholder',
  firstLevelTitle: 'dbh_region_cascader_select_region',
  secondLevelTitle: 'dbh_country',
  multiCountryText: 'dbh_region_cascader_select_multi_country',
};

export function getRegionName(region?: IRegion | null, locale?: any): string {
  if (isChineseLocale(locale)) {
    return region?.area_name_ch || region?.area_name_en || region?.area_code || '';
  }

  return region?.area_name_en || region?.area_name_ch || region?.area_code || '';
}

export function getCountryName(country?: ICountry | null, locale?: any): string {
  if (isChineseLocale(locale)) {
    return country?.country_name_ch || country?.country_name_en || country?.country_code || '';
  }

  return country?.country_name_en || country?.country_name_ch || country?.country_code || '';
}

export function getRegionOptions(regions?: IRegion[] | null, locale?: any): IRegionCascaderSelectOption[] {
  if (!Array.isArray(regions) || regions.length === 0) {
    return [];
  }

  return regions.map((region) => {
    const countries = region?.country_set || [];
    const regionName = getRegionName(region, locale);
    const children = countries.map((country) => {
      const countryName = getCountryName(country, locale);

      return {
        value: country?.country_code || '',
        text: countryName,
        icon: getCountryFlag(country?.country_code || ''),
        region,
        country,
      };
    });

    return {
      value: region?.area_code || regionName,
      text: regionName,
      children,
      region,
    };
  });
}

export function getSelectedCountry(regions?: IRegion[] | null, value?: string): IRegionCascaderSelectChangeContext {
  if (!Array.isArray(regions) || regions.length === 0) {
    return {
      path: [],
    };
  }

  if (!value) {
    return {
      path: [],
    };
  }

  for (const region of regions) {
    const countries = region?.country_set || [];
    const country = countries.find((item) => item?.country_code === value);

    if (country) {
      return {
        region,
        country,
        path: [],
      };
    }
  }

  return {
    path: [],
  };
}

export function getRegionChangeContext(
  context?: ICascaderSelectChangeContext | null,
): IRegionCascaderSelectChangeContext {
  const selectedOption = context?.selectedOption as IRegionCascaderSelectOption | undefined;
  const parentOption = context?.parentOption as IRegionCascaderSelectOption | undefined;

  return {
    region: selectedOption?.region || parentOption?.region,
    country: selectedOption?.country,
    path: (context?.path || []) as IRegionCascaderSelectOption[],
  };
}

function isChineseLocale(locale?: any): boolean {
  if (typeof locale !== 'string') {
    return false;
  }

  const realLocale = locale.toLowerCase().replace('-', '_');

  return realLocale === 'cn' || realLocale.startsWith('zh');
}
